import gsap from 'gsap';
import { computed, createApp, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';

import * as AnalyticsData from '~/analytics/analytics-data';
import { useCart } from '~/composables/use-cart';
import { useCartDrawerVisibility } from '~/composables/use-cart-drawer-visibility';
import { useDesktopMenu } from '~/composables/use-desktop-menu';
import { useHeaderDesktopFocus } from '~/composables/use-header-desktop-focus';
import { useHeaderMobileFocus } from '~/composables/use-header-mobile-focus';
import { useMobileMenu } from '~/composables/use-mobile-menu';
import { useScrollJackSection } from '~/composables/use-scroll-jack-section';
import { useScrollLock } from '~/composables/use-scroll-lock';
import { useViewportSize } from '~/composables/use-viewport-size';
import { ANIMATION_DURATIONS, ANIMATION_EASES } from '~/constants/animations';
import { TRACK_NAVIGATION_LEVEL } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { safeKill } from '~/utils/animations';
import { initSwiper } from '~/utils/swiper';

import { BaseSection } from './base-section';

const MENU_HOVER_DELAY = 0.4;
const MOUSE_TRIGGER_THRESHOLD = 60; // Show header when mouse is within 60px of top
const SECTION_NAME = 'header';
class Header extends BaseSection {
  constructor() {
    super(`[data-section-type="${SECTION_NAME}"]`);
  }

  init(instanceData) {
    const container = instanceData.container;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Header - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    createApp({
      name: 'HeaderSection',
      setup() {
        const { isLarge } = useViewportSize();
        const { lockScroll, unlockScroll } = useScrollLock();
        const { cart } = useCart();
        const cartVisibility = useCartDrawerVisibility();
        const mobileMenu = useMobileMenu();
        const desktopMenu = useDesktopMenu();
        const { isInScrollJackSection } = useScrollJackSection();

        const lastTriggerElement = ref(null);
        const lastMobileTriggerElement = ref(null);
        const cartCount = computed(() => cart.value?.item_count || 0);

        const elements = ref({});
        const timelines = ref({});
        const timers = ref({});
        const swiperInstances = ref({});
        const navButtonKeydownHandlers = ref({});

        const lastScrollY = ref(0);
        const headerHeight = ref(0);
        const isHeaderHiddenByScroll = ref(false);
        const isMouseNearTop = ref(false);
        const showCartCount = computed(() => cartCount.value > 0);

        const shouldHeaderBeVisible = computed(() => {
          const isMenuOpen = mobileMenu.isMobileOpen.value || desktopMenu.isDesktopMenuOpen.value;
          return (
            isMenuOpen ||
            lastScrollY.value < headerHeight.value ||
            !isHeaderHiddenByScroll.value ||
            isMouseNearTop.value
          );
        });

        onMounted(() => {
          findElements();
          calculateHeaderHeight();
          setInitialMenuStates();
          createAnimations();
          setupSharedListeners();
          handleResize(isLarge.value, undefined);

          timelines.value.contentFadeIn?.play();

          // Analytics - Hook Query String API events for this section
          AnalyticsData.hookNavigationClicked({ sectionName: SECTION_NAME });
          AnalyticsData.hookMiniNavClicked({ sectionName: SECTION_NAME });
        });

        onUnmounted(() => {
          removeAllListeners();
          cleanup();
        });

        watch(isLarge, (newVal, oldVal) => {
          handleResize(newVal, oldVal);
        });

        watch(isInScrollJackSection, (inSection) => {
          if (inSection) {
            isHeaderHiddenByScroll.value = true;
          }
        });

        watch(shouldHeaderBeVisible, (isVisible) => {
          if (!elements.value.headerBar) return;
          const offset = 20; // 20px offset to account for top-5
          const targetY = isVisible ? 0 : -(headerHeight.value + offset);

          gsap.to(elements.value.headerBar, {
            duration: ANIMATION_DURATIONS.ELEMENT.NAVIGATION.DROPDOWN,
            ease: ANIMATION_EASES.POWER3_IN_OUT,
            y: targetY,
          });
        });

        const findElements = () => {
          elements.value = {
            allCategoryContentDivs: Array.from(
              container.querySelectorAll('[data-category-content]'),
            ),
            allCategoryImagesDivs: Array.from(container.querySelectorAll('[data-category-images]')),
            allDesktopPanels: Array.from(
              container.querySelectorAll('[data-desktop-submenu-panel]'),
            ),
            allSubmenuPanels: Array.from(container.querySelectorAll('[data-submenu]')),
            container,
            desktopHeaderContent: container.querySelector('[data-header-content-desktop]'),
            desktopMenu: container.querySelector('[data-desktop-menu]'),
            desktopMenuContentWrapper: container.querySelector(
              '[data-desktop-menu-content-wrapper]',
            ),
            desktopMenuDropdownBackground: container.querySelector(
              '[data-desktop-menu-dropdown-background]',
            ),
            desktopMenuOverlay: container.querySelector('[data-desktop-menu-overlay]'),
            desktopNavButtons: container.querySelectorAll(
              '[data-header-content-desktop] button[data-menu-trigger]',
            ),
            desktopSecondaryNavigationBars: container.querySelectorAll(
              '[data-desktop-secondary-navigation-bar]',
            ),
            desktopSubmenuPanelContents: container.querySelectorAll(
              '[data-desktop-submenu-panel-content]',
            ),
            headerBar: container.querySelector('[data-header-bar]'),
            level1Container: container.querySelector('[data-submenu-level="1"]'),
            level2Container: container.querySelector('[data-submenu-level="2"]'),
            mainMenuContainer: container.querySelector('[data-submenu-level="0"]'),
            mobileHeaderContent: container.querySelector('[data-header-content-mobile]'),
            mobileMenu: container.querySelector('[data-mobile-menu]'),
            mobileMenuFooter: container.querySelector('[data-mobile-menu-footer]'),
            mobileMenuItems: Array.from(container.querySelectorAll('[data-mobile-menu-item]')),
            mobileMenuOverlay: container.querySelector('[data-mobile-menu-overlay]'),
            mobileMenuToggle: container.querySelector('[data-testid="mobile-menu-toggle"]'),
          };
        };

        const createAnimations = () => {
          timelines.value.mobileOverlay = createMobileOverlayTimeline();
          timelines.value.desktopDropdown = createDesktopDropdownTimeline();
          timelines.value.contentFadeIn = createContentFadeInTimeline();
        };

        const setupSharedListeners = () => {
          setupKeyboardListeners();
          setupScrollListener();
          setupMouseListener();
          setupNavButtonListeners();
        };

        const setupKeyboardListeners = () => {
          window.addEventListener('keydown', handleEscKey);
        };

        const setupScrollListener = () => {
          window.addEventListener('scroll', handleScroll, { passive: true });
        };

        const setupMouseListener = () => {
          window.addEventListener('mousemove', handleMouseMove, { passive: true });
        };

        const setupNavButtonListeners = () => {
          navButtonKeydownHandlers.value = {};
          elements.value.desktopNavButtons?.forEach((button) => {
            const menuId = button.dataset.menuTrigger;
            if (menuId) {
              const handler = (event) => handleNavButtonKeyDown(event, menuId, button);
              navButtonKeydownHandlers.value[menuId] = handler;
              button.addEventListener('keydown', handler);
            }
          });
        };

        const removeAllListeners = () => {
          window.removeEventListener('keydown', handleEscKey);
          window.removeEventListener('scroll', handleScroll);
          window.removeEventListener('mousemove', handleMouseMove);

          elements.value.desktopNavButtons?.forEach((button) => {
            const menuId = button.dataset.menuTrigger;
            if (menuId && navButtonKeydownHandlers.value[menuId]) {
              button.removeEventListener('keydown', navButtonKeydownHandlers.value[menuId]);
            }
          });
          navButtonKeydownHandlers.value = {};
        };

        function handleResize(newIsLarge, oldIsLarge) {
          if (newIsLarge === oldIsLarge) return;

          // Reset header position and recalculate height on resize
          if (elements.value.headerBar) {
            gsap.set(elements.value.headerBar, { y: 0 });
          }
          isHeaderHiddenByScroll.value = false; // Ensure header is visible after resize
          calculateHeaderHeight();

          if (newIsLarge) {
            if (mobileMenu.isMobileOpen.value) {
              mobileMenu.closeMobileMenu();
              unlockScroll();
              gsap.set(elements.value.mobileMenu, { pointerEvents: 'none' });
              gsap.set(elements.value.mobileMenuOverlay, { scaleY: 0 });
              gsap.set(elements.value.mainMenuContainer, { opacity: 0, pointerEvents: 'none' });
              const resetProps = { opacity: 0, pointerEvents: 'none', xPercent: 0 };
              gsap.set(elements.value.allSubmenuPanels, resetProps);
              gsap.set(
                [elements.value.level1Container, elements.value.level2Container],
                resetProps,
              );
            }
            destroyAllCarousels();
          } else {
            if (desktopMenu.isDesktopMenuOpen.value) {
              desktopMenu.closeDesktopMenu();
              gsap.set(elements.value.allDesktopPanels, { display: 'none' });
              gsap.set(elements.value.desktopMenu, { pointerEvents: 'none' });
              gsap.set(elements.value.desktopMenuOverlay, { opacity: 0 });
              gsap.set(elements.value.desktopMenuDropdownBackground, { scaleY: 0 });
              gsap.set(elements.value.desktopMenuContentWrapper, { opacity: 0 });
            }
            clearTimers();
          }
        }

        const handleEscKey = (event) => {
          if (event.key === 'Escape') {
            if (isLarge.value && desktopMenu.isDesktopMenuOpen.value) {
              closeDesktopMenuHandler();
            } else if (!isLarge.value && mobileMenu.isMobileOpen.value) {
              toggleMobileMenuHandler();
            }
          }
        };

        const createContentFadeInTimeline = () => {
          const { desktopHeaderContent, mobileHeaderContent } = elements.value;
          if (!mobileHeaderContent || !desktopHeaderContent) return null;

          return gsap.to([mobileHeaderContent, desktopHeaderContent], {
            duration: ANIMATION_DURATIONS.SECTION.FADE_IN,
            ease: ANIMATION_EASES.SINE_OUT,
            opacity: 1,
            paused: true,
          });
        };

        const createMobileOverlayTimeline = () => {
          const {
            mainMenuContainer,
            mobileMenu,
            mobileMenuFooter,
            mobileMenuItems,
            mobileMenuOverlay,
          } = elements.value;
          if (
            !mobileMenu ||
            !mobileMenuOverlay ||
            !mobileMenuItems?.length ||
            !mobileMenuFooter ||
            !mainMenuContainer
          ) {
            return null;
          }
          const duration = ANIMATION_DURATIONS.ELEMENT.NAVIGATION.DROPDOWN;
          const timeline = gsap.timeline({
            onReverseComplete: () => {
              gsap.set(mobileMenu, { pointerEvents: 'none' });

              const resetProps = {
                display: 'none',
                opacity: 0,
                pointerEvents: 'none',
                xPercent: 0,
              };
              gsap.set(elements.value.allSubmenuPanels, resetProps);
              gsap.set(
                [
                  elements.value.mainMenuContainer,
                  elements.value.level1Container,
                  elements.value.level2Container,
                ],
                resetProps,
              );
            },
            paused: true,
          });

          timeline.set(mainMenuContainer, { display: 'flex' }, 0);
          timeline.to(
            mainMenuContainer,
            {
              duration: duration * 0.8,
              ease: ANIMATION_EASES.SINE_OUT,
              opacity: 1,
              pointerEvents: 'auto',
            },
            0,
          );
          timeline.fromTo(
            mobileMenuOverlay,
            { scaleY: 0 },
            { duration, ease: ANIMATION_EASES.POWER3_IN_OUT, scaleY: 1, transformOrigin: 'top' },
            0,
          );
          timeline.fromTo(
            mobileMenuItems,
            { opacity: 0, pointerEvents: 'none' },
            {
              duration,
              ease: ANIMATION_EASES.SINE_OUT,
              opacity: 1,
              pointerEvents: 'auto',
              stagger: 0.05,
            },
            duration + 0.1,
          );
          timeline.fromTo(
            mobileMenuFooter,
            { opacity: 0, pointerEvents: 'none' },
            { duration, ease: ANIMATION_EASES.SINE_OUT, opacity: 1, pointerEvents: 'auto' },
            duration + 0.1,
          );

          return timeline;
        };

        const createDesktopDropdownTimeline = () => {
          const {
            allDesktopPanels,
            desktopMenu: desktopMenuElement,
            desktopMenuContentWrapper,
            desktopMenuDropdownBackground,
            desktopMenuOverlay,
          } = elements.value;

          if (
            !desktopMenuElement ||
            !desktopMenuContentWrapper ||
            !desktopMenuOverlay ||
            !desktopMenuDropdownBackground
          ) {
            return null;
          }
          const duration = ANIMATION_DURATIONS.ELEMENT.NAVIGATION.DROPDOWN;
          const timeline = gsap.timeline({
            onReverseComplete: () => {
              gsap.set(allDesktopPanels, { display: 'none' });
              gsap.set(desktopMenuElement, { pointerEvents: 'none' });
              desktopMenu.closeDesktopMenu();
            },
            onStart: () => {
              gsap.set(desktopMenuElement, { pointerEvents: 'auto' });
            },
            paused: true,
          });

          timeline.fromTo(
            desktopMenuOverlay,
            { opacity: 0 },
            { duration: duration * 0.8, ease: ANIMATION_EASES.SINE_OUT, opacity: 1 },
            0,
          );

          timeline.fromTo(
            desktopMenuDropdownBackground,
            { scaleY: 0 },
            { duration, ease: ANIMATION_EASES.POWER3_IN_OUT, scaleY: 1, transformOrigin: 'top' },
            0,
          );

          timeline.fromTo(
            desktopMenuContentWrapper,
            { opacity: 0 },
            { duration: duration * 0.5, ease: ANIMATION_EASES.SINE_OUT, opacity: 1 },
            duration * 0.5,
          );

          timeline.fromTo(
            [
              ...elements.value.desktopSecondaryNavigationBars,
              ...elements.value.desktopSubmenuPanelContents,
            ],
            { opacity: 0 },
            {
              duration: duration * 0.85,
              ease: ANIMATION_EASES.SINE_OUT,
              opacity: 1,
              stagger: 0.05,
            },
            duration * 0.85,
          );

          return timeline;
        };

        const getPanelId = (panelElement) => {
          return (
            panelElement?.dataset?.submenu || panelElement?.dataset?.desktopSubmenuPanel || null
          );
        };

        const initializeCarouselIfNeeded = (panelElement) => {
          if (!panelElement || isLarge.value) return;
          const carouselElement = panelElement.querySelector('.menu-image-carousel');
          const panelId = getPanelId(panelElement);
          if (carouselElement && panelId && !swiperInstances.value[panelId]) {
            const swiperInstance = initSwiper(panelElement, {
              slidesPerView: 2.3,
              spaceBetween: 10,
            });

            if (!swiperInstance) {
              DataDogLogs.error(
                '[Allbirds section] - Header - initializeCarouselIfNeeded - Failed to initialize Swiper',
                { panelElement, panelId },
              );
              return;
            }

            swiperInstances.value[panelId] = swiperInstance;
            gsap.to(carouselElement, { duration: 0.3, ease: 'sine.out', opacity: 1 });
          }
        };

        const destroyCarousel = (panelElement) => {
          if (isLarge.value) return;
          const panelId = getPanelId(panelElement);
          if (panelId && swiperInstances.value[panelId]) {
            const carouselElement = panelElement?.querySelector('.menu-image-carousel');
            if (carouselElement) gsap.set(carouselElement, { opacity: 0 });
            try {
              swiperInstances.value[panelId].destroy(true, true);
            } catch (e) {
              DataDogLogs.error(
                '[Allbirds section] - Header - destroyCarousel - Error destroying carousel',
                {
                  panelElement,
                },
                e,
              );
            }
            delete swiperInstances.value[panelId];
          }
        };

        const destroyAllCarousels = () => {
          if (isLarge.value) return;
          Object.keys(swiperInstances.value).forEach((panelId) => {
            try {
              swiperInstances.value[panelId]?.destroy(true, true);
            } catch (e) {
              DataDogLogs.error(
                '[Allbirds section] - Header - destroyAllCarousels - Error destroying carousel',
                {
                  panelId,
                },
                e,
              );
            }
          });
          swiperInstances.value = {};
        };

        const initializeAccordionsInPanel = (panelElement) => {
          if (!panelElement || isLarge.value) return;
          const accordions = panelElement.querySelectorAll('[data-accordion-content]');
          if (!accordions.length) return;
          const accordionIds = Array.from(accordions).map((el) => el.dataset.accordionContent);
          mobileMenu.setAccordionsOpenByDefault(accordionIds);
          Array.from(accordions).forEach((accordion) => {
            if (mobileMenu.isAccordionOpen(accordion.dataset.accordionContent)) {
              gsap.set(accordion, { maxHeight: `${accordion.scrollHeight}px` });
            } else {
              gsap.set(accordion, { maxHeight: '0px' });
            }
          });
        };

        const slideTransition = (currentElement, nextElement, direction) => {
          if (!currentElement || !nextElement) return;
          gsap.killTweensOf([currentElement, nextElement]);
          const currentParentContainer = currentElement.closest('[data-submenu-level]');
          const nextParentContainer = nextElement.closest('[data-submenu-level]');
          gsap.killTweensOf([currentParentContainer, nextParentContainer]);
          gsap.set(nextElement, { display: 'flex' });
          if (nextParentContainer) gsap.set(nextParentContainer, { display: 'flex' });
          const timeline = gsap.timeline({
            onComplete: () => {
              destroyCarousel(currentElement);
              initializeCarouselIfNeeded(nextElement);
              gsap.set(currentElement, { display: 'none' });
              if (currentParentContainer && currentParentContainer !== nextParentContainer) {
                gsap.set(currentParentContainer, { display: 'none' });
              }

              nextTick(() => {
                let elementToFocus = null;
                if (direction === 'backward' && lastMobileTriggerElement.value) {
                  elementToFocus = lastMobileTriggerElement.value;
                  lastMobileTriggerElement.value = null;
                } else {
                  elementToFocus =
                    nextElement.querySelector('button[aria-label*="Back"]') ||
                    nextElement.querySelector('button:not([disabled]), a[href]:not([disabled])') ||
                    nextElement.querySelector(
                      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                    );
                }

                if (elementToFocus) {
                  elementToFocus.focus();
                }
              });
            },
          });
          if (nextParentContainer) {
            gsap.set(nextParentContainer, { opacity: 0, pointerEvents: 'none', xPercent: 0 });
            timeline.to(
              nextParentContainer,
              { duration: 0.1, ease: 'none', opacity: 1, pointerEvents: 'auto' },
              0,
            );
          }
          const animations = {
            backward: { current: { xPercent: 100 }, next: { fromXPercent: -100, toXPercent: 0 } },
            forward: { current: { xPercent: -100 }, next: { fromXPercent: 100, toXPercent: 0 } },
          };
          const config = animations[direction];
          const slideDuration = ANIMATION_DURATIONS.ELEMENT.NAVIGATION.SLIDE;
          const slideEase = ANIMATION_EASES.POWER3_IN_OUT;
          const fadeDuration = slideDuration * 0.5;
          timeline.to(
            currentElement,
            {
              duration: slideDuration,
              ease: slideEase,
              pointerEvents: 'none',
              xPercent: config.current.xPercent,
            },
            0,
          );
          timeline.to(
            currentElement,
            { duration: fadeDuration, ease: ANIMATION_EASES.SINE_IN, opacity: 0 },
            0,
          );
          timeline.fromTo(
            nextElement,
            { pointerEvents: 'none', xPercent: config.next.fromXPercent },
            {
              duration: slideDuration,
              ease: slideEase,
              pointerEvents: 'auto',
              xPercent: config.next.toXPercent,
            },
            0,
          );
          timeline.fromTo(
            nextElement,
            { opacity: 0 },
            { duration: fadeDuration, ease: ANIMATION_EASES.SINE_OUT, opacity: 1 },
            0,
          );
          timeline.play();
        };

        const createHideAllSubmenusTimeline = () => {
          const { allSubmenuPanels, level1Container, level2Container } = elements.value;
          const hasSubmenuPanels = allSubmenuPanels.length > 0;
          const timeline = gsap.timeline({
            onComplete: () => {
              const resetProps = {
                display: 'none',
                opacity: 0,
                pointerEvents: 'none',
                xPercent: 0,
              };
              if (level1Container) gsap.set(level1Container, resetProps);
              if (level2Container) gsap.set(level2Container, resetProps);
              if (hasSubmenuPanels) gsap.set(allSubmenuPanels, resetProps);
            },
            paused: true,
          });
          const animProps = {
            duration: ANIMATION_DURATIONS.ELEMENT.NAVIGATION.SLIDE * 0.8,
            ease: ANIMATION_EASES.POWER3_IN_OUT,
            opacity: 0,
            pointerEvents: 'none',
          };
          const targetsToHide = [];
          if (hasSubmenuPanels) targetsToHide.push(...allSubmenuPanels);
          if (level1Container) targetsToHide.push(level1Container);
          if (level2Container) targetsToHide.push(level2Container);
          if (targetsToHide.length > 0)
            timeline.to(targetsToHide, { ...animProps, stagger: 0.03 }, 0);
          return timeline;
        };

        const toggleMobileMenuHandler = () => {
          if (isLarge.value) return;
          if (desktopMenu.isDesktopMenuOpen.value) closeDesktopMenuHandler();
          const wasOpen = mobileMenu.isMobileOpen.value;
          mobileMenu.toggleMobileMenuState();
          if (!wasOpen) {
            lockScroll();
            gsap.set(elements.value.mobileMenu, { pointerEvents: 'auto' });

            const hiddenPanelProps = { display: 'none', opacity: 0, pointerEvents: 'none' };
            const hiddenItemProps = { opacity: 0, pointerEvents: 'none' };

            gsap.set(elements.value.allSubmenuPanels, { ...hiddenPanelProps, xPercent: 100 });
            if (elements.value.level1Container)
              gsap.set(elements.value.level1Container, { ...hiddenPanelProps, xPercent: 0 });
            if (elements.value.level2Container)
              gsap.set(elements.value.level2Container, { ...hiddenPanelProps, xPercent: 0 });

            gsap.set(
              [...elements.value.mobileMenuItems, elements.value.mobileMenuFooter],
              hiddenItemProps,
            );
            gsap.set(elements.value.mainMenuContainer, {
              display: 'flex',
              opacity: 0,
              pointerEvents: 'none',
              xPercent: 0,
            });

            timelines.value.mobileOverlay?.invalidate().restart();

            const overlayDuration = timelines.value.mobileOverlay?.duration() || 0.5;
            gsap.delayedCall(overlayDuration, () =>
              initializeCarouselIfNeeded(elements.value.mainMenuContainer),
            );
          } else {
            unlockScroll();
            createHideAllSubmenusTimeline().play();
            timelines.value.mobileOverlay?.reverse();
            destroyAllCarousels();
          }
        };

        const toggleMobileSelectedMenu = (menuId, submenuToClose = undefined, event) => {
          // Send analytics,
          // if event is defined then is moving forward and type is global otherwise it's back
          // if submenuToClose is defined then add navigationLevel2 with the last part of the id
          AnalyticsData.triggerNavigationViewed({
            navigationLevel1: menuId,
            navigationType: event ? TRACK_NAVIGATION_LEVEL.GLOBAL : TRACK_NAVIGATION_LEVEL.BACK,
            [submenuToClose && 'navigationLevel2']: new String(submenuToClose).split('_').pop(),
          });

          if (isLarge.value) return;
          const { level1Container, level2Container, mainMenuContainer } = elements.value;
          if (!mainMenuContainer || !level1Container) return;

          const selectedMenu = level1Container.querySelector(`[data-submenu="${menuId}"]`);
          if (!selectedMenu) return;

          if (submenuToClose) {
            if (!level2Container) return;
            const subMenuToCloseElement = level2Container.querySelector(
              `[data-submenu="${submenuToClose}"]`,
            );
            if (!subMenuToCloseElement) return;
            mobileMenu.setMenuStateActive(menuId);
            slideTransition(subMenuToCloseElement, selectedMenu, 'backward');
            return;
          }

          if (event?.currentTarget) {
            lastMobileTriggerElement.value = event.currentTarget;
          }

          const isOpening = !mobileMenu.isMenuOpen(menuId);
          mobileMenu.toggleMenuState(menuId);
          if (isOpening) slideTransition(mainMenuContainer, selectedMenu, 'forward');
          else slideTransition(selectedMenu, mainMenuContainer, 'backward');
          if (level2Container) gsap.set(level2Container, { opacity: 0, pointerEvents: 'none' });
        };

        const toggleMobileSelectedSubMenu = (submenuId, parentMenuId, parentMenuL2Label, event) => {
          // Send analytics when the submenu is opened
          AnalyticsData.triggerNavigationViewed({
            navigationLevel1: parentMenuId,
            navigationLevel2: parentMenuL2Label,
            navigationType: TRACK_NAVIGATION_LEVEL.LEVEL_2,
          });

          if (isLarge.value) return;
          const { level1Container, level2Container } = elements.value;
          if (!level1Container || !level2Container) return;
          const parentMenu = level1Container.querySelector(`[data-submenu="${parentMenuId}"]`);
          if (!parentMenu) return;
          const selectedSubMenu = level2Container.querySelector(`[data-submenu="${submenuId}"]`);
          if (!selectedSubMenu) return;

          if (event?.currentTarget) {
            lastMobileTriggerElement.value = event.currentTarget;
          }

          const isOpeningSubMenu = !mobileMenu.isSubMenuOpen(submenuId);
          mobileMenu.toggleSubMenuState(submenuId);
          if (isOpeningSubMenu) {
            slideTransition(parentMenu, selectedSubMenu, 'forward');
            initializeAccordionsInPanel(selectedSubMenu);
          }
        };

        const toggleMobileAccordionMenu = (
          accordionId,
          parentMenuLabel,
          parentMenuL2Label,
          menuL3Label,
        ) => {
          // Send analytics when user interacts with the accordion
          AnalyticsData.triggerNavigationViewed({
            navigationLevel1: parentMenuLabel,
            navigationLevel2: parentMenuL2Label,
            navigationLevel3: menuL3Label,
            navigationType: TRACK_NAVIGATION_LEVEL.LEVEL_3,
          });

          if (isLarge.value) return;
          const accordionContent = elements.value.container.querySelector(
            `[data-accordion-content="${accordionId}"]`,
          );
          if (!accordionContent) return;
          const isOpen = mobileMenu.isAccordionOpen(accordionId);
          mobileMenu.toggleAccordionState(accordionId);
          gsap.to(accordionContent, {
            duration: ANIMATION_DURATIONS.ELEMENT.NAVIGATION.DROPDOWN * 0.5,
            ease: ANIMATION_EASES.POWER3_IN_OUT,
            maxHeight: isOpen ? '0px' : `${accordionContent.scrollHeight}px`,
            onComplete: () => {
              if (!isOpen) gsap.set(accordionContent, { maxHeight: 'auto' });
            },
          });
        };

        // Re-add the fade function (modified slightly)
        const fadeCategoryElements = (categoryId, fadeIn = true) => {
          if (!categoryId || !elements.value.container) return;
          const targetPanel = elements.value.container.querySelector(
            `[data-testid="desktop-submenu-panel-${desktopMenu.activeDesktopMenu.value}"]`,
          );
          if (!targetPanel) return;

          const categoryContent = targetPanel.querySelector(
            `[data-testid="desktop-category-content-${categoryId}"]`,
          );
          const categoryImages = targetPanel.querySelector(
            `[data-testid="desktop-category-images-${categoryId}"]`,
          );

          const elementsToAnimate = [];
          if (categoryContent) elementsToAnimate.push(categoryContent);
          if (categoryImages) elementsToAnimate.push(categoryImages);

          if (elementsToAnimate.length > 0) {
            gsap.to(elementsToAnimate, {
              duration: ANIMATION_DURATIONS.ELEMENT.NAVIGATION.DROPDOWN,
              ease: fadeIn ? ANIMATION_EASES.SINE_OUT : ANIMATION_EASES.SINE_IN,
              opacity: fadeIn ? 1 : 0,
              stagger: 0.25,
            });
          }
        };

        watch(desktopMenu.activeDesktopCategory, (newCategoryId, oldCategoryId) => {
          if (desktopMenu.isDesktopMenuOpen.value && newCategoryId !== oldCategoryId) {
            if (oldCategoryId) {
              fadeCategoryElements(oldCategoryId, false);
            }
            if (newCategoryId) {
              const newPanel = elements.value.container.querySelector(
                `[data-testid="desktop-submenu-panel-${desktopMenu.activeDesktopMenu.value}"]`,
              );
              const newContent = newPanel?.querySelector(
                `[data-testid="desktop-category-content-${newCategoryId}"]`,
              );
              const newImages = newPanel?.querySelector(
                `[data-testid="desktop-category-images-${newCategoryId}"]`,
              );
              const elementsToFadeIn = [newContent, newImages].filter(Boolean);
              if (elementsToFadeIn.length > 0) {
                gsap.set(elementsToFadeIn, { opacity: 0 });
                nextTick(() => {
                  fadeCategoryElements(newCategoryId, true);
                });
              }
            }
          }
        });

        const findDefaultCategoryForMenu = (menuId) => {
          if (!menuId) return null;
          const menuPanel = elements.value.container.querySelector(
            `[data-desktop-submenu-panel="${menuId}"]`,
          );
          return menuPanel?.dataset?.defaultCategory || null;
        };

        const handleDesktopMenuToggle = (menuId) => {
          if (!isLarge.value) return;
          const wasOpen = desktopMenu.isDesktopMenuOpen.value;
          const isSameMenu = wasOpen && desktopMenu.activeDesktopMenu.value === menuId;
          if (isSameMenu) {
            closeDesktopMenuHandler();
          } else {
            const defaultCategoryId = findDefaultCategoryForMenu(menuId);
            if (!wasOpen) {
              const targetPanel = elements.value.container.querySelector(
                `[data-desktop-submenu-panel="${menuId}"]`,
              );
              if (!targetPanel) return;
              desktopMenu.openDesktopMenu(menuId);
              desktopMenu.setActiveDesktopCategory(defaultCategoryId);

              timelines.value.desktopDropdown?.invalidate().timeScale(1).restart();
            } else {
              const oldMenuId = desktopMenu.activeDesktopMenu.value;
              const oldMenuPanel = elements.value.container.querySelector(
                `[data-desktop-submenu-panel="${oldMenuId}"]`,
              );
              const elementsToFadeOut = [];
              if (oldMenuPanel) {
                const oldPanelContentWrapper = oldMenuPanel.querySelector(
                  `[data-desktop-submenu-panel-content]`,
                );
                const oldSecondaryNav = oldMenuPanel.querySelector(
                  `[data-desktop-secondary-navigation-bar]`,
                );
                if (oldPanelContentWrapper) elementsToFadeOut.push(oldPanelContentWrapper);
                if (oldSecondaryNav) elementsToFadeOut.push(oldSecondaryNav);
              }
              gsap.to(elementsToFadeOut, {
                duration: ANIMATION_DURATIONS.ELEMENT.NAVIGATION.DROPDOWN * 0.5,
                ease: ANIMATION_EASES.SINE_IN,
                onComplete: async () => {
                  desktopMenu.openDesktopMenu(menuId);
                  desktopMenu.setActiveDesktopCategory(defaultCategoryId);
                  await nextTick();
                  const targetPanel = elements.value.container.querySelector(
                    `[data-desktop-submenu-panel="${menuId}"]`,
                  );
                  if (!targetPanel) return;
                  // Fade in the new panel's secondary nav and content wrapper
                  const targetPanelContentWrapper = targetPanel.querySelector(
                    `[data-desktop-submenu-panel-content]`,
                  );
                  const newSecondaryNav = targetPanel.querySelector(
                    `[data-desktop-secondary-navigation-bar]`,
                  );
                  const elementsToFadeIn = [];
                  if (targetPanelContentWrapper) elementsToFadeIn.push(targetPanelContentWrapper);
                  if (newSecondaryNav) elementsToFadeIn.push(newSecondaryNav);
                  gsap.fromTo(
                    elementsToFadeIn,
                    { opacity: 0 },
                    {
                      duration: ANIMATION_DURATIONS.ELEMENT.NAVIGATION.DROPDOWN * 0.5,
                      ease: ANIMATION_EASES.SINE_OUT,
                      opacity: 1,
                      stagger: 0.05,
                    },
                  );
                },
                opacity: 0,
              });
            }
          }
        };

        // --- Helper function for useHeaderDesktopFocus ---
        const getLastTriggerElement = () => lastTriggerElement.value;
        const getPanelElement = (menuId) => {
          if (!menuId) return null;
          return elements.value.container?.querySelector(
            `[data-testid="desktop-submenu-panel-${menuId}"]`,
          );
        };

        // Initialize focus management for desktop and mobile
        const { prepareActivation: prepareDesktopFocusActivation } = useHeaderDesktopFocus({
          activeDesktopMenu: desktopMenu.activeDesktopMenu,
          getLastTriggerElement,
          getPanelElement,
          isDesktopMenuOpen: desktopMenu.isDesktopMenuOpen,
          setActiveCategory: desktopMenu.setActiveDesktopCategory,
        });

        useHeaderMobileFocus({
          getMobileMenuElement: () => elements.value.mobileMenu,
          getMobileTriggerElement: () => elements.value.mobileMenuToggle,
          isMobileOpen: mobileMenu.isMobileOpen,
        });

        // --- Event Handlers ---
        const handleNavButtonKeyDown = (event, menuId, buttonElement) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            lastTriggerElement.value = buttonElement;
            prepareDesktopFocusActivation('keyboard');
            handleDesktopMenuToggle(menuId);
          }
        };

        const handleDesktopMenuMouseEnter = (menuId) => {
          if (!isLarge.value) return;
          const triggerButton = elements.value.container?.querySelector(
            `button[data-menu-trigger="${menuId}"]`,
          );
          if (triggerButton) {
            lastTriggerElement.value = triggerButton;
          }

          safeKill(timers.value.categoryHover);
          desktopMenu.setHoveredDesktopCategory(null);
          desktopMenu.setHoveredDesktopMenu(menuId);
          safeKill(timers.value.close);
          if (
            desktopMenu.isDesktopMenuOpen.value &&
            desktopMenu.activeDesktopMenu.value === menuId
          ) {
            safeKill(timers.value.open);
            return;
          }
          safeKill(timers.value.open);
          prepareDesktopFocusActivation('hover');
          timers.value.open = gsap.delayedCall(MENU_HOVER_DELAY, () => {
            if (triggerButton) lastTriggerElement.value = triggerButton;
            handleDesktopMenuToggle(menuId);

            // Trigger event when hovering over the desktop main nav or L1 (global)
            AnalyticsData.triggerNavigationViewed({
              navigationLevel1: menuId,
              navigationType: TRACK_NAVIGATION_LEVEL.GLOBAL,
            });
          });
        };

        const handleDesktopMenuMouseLeave = () => {
          if (!isLarge.value) return;

          // Kill the open timer to prevent menu from opening
          safeKill(timers.value.open);

          // Clear hovered menu state
          desktopMenu.setHoveredDesktopMenu(null);
        };

        const handlePanelMouseEnter = () => {
          if (!isLarge.value) return;
          safeKill(timers.value.close);
        };

        const handlePanelMouseLeave = (e) => {
          const isStillInPanel = e.relatedTarget == elements.value.desktopMenuDropdownBackground;
          if (!isLarge.value || isStillInPanel) return;
          // Clear category timer and hovered category when leaving the panel area
          safeKill(timers.value.categoryHover);
          desktopMenu.setHoveredDesktopCategory(null);
          startCloseTimer();
        };

        const handleCategoryMouseEnter = (categoryId, menuId, menuLevel2Id) => {
          // Immediately set the hovered category state
          desktopMenu.setHoveredDesktopCategory(categoryId);
          // Clear any existing category hover timer
          safeKill(timers.value.categoryHover);
          // Only set a new timer if the category is different from the active one
          if (desktopMenu.activeDesktopCategory.value !== categoryId) {
            timers.value.categoryHover = gsap.delayedCall(MENU_HOVER_DELAY, () => {
              desktopMenu.setActiveDesktopCategory(categoryId);

              // Trigger event when hovering nav menu L2
              AnalyticsData.triggerNavigationViewed({
                navigationLevel1: menuId,
                navigationLevel2: menuLevel2Id,
                navigationType: TRACK_NAVIGATION_LEVEL.LEVEL_2,
              });
            });
          }
        };

        const startCloseTimer = () => {
          if (!isLarge.value || !desktopMenu.isDesktopMenuOpen.value) return;
          safeKill(timers.value.close);
          timers.value.close = gsap.delayedCall(MENU_HOVER_DELAY, closeDesktopMenuHandler);
        };

        const closeDesktopMenuHandler = () => {
          if (!isLarge.value || !desktopMenu.isDesktopMenuOpen.value) return;
          safeKill(timers.value.open);
          safeKill(timers.value.close);
          timelines.value.desktopDropdown?.timeScale(1.5).reverse();
          // Clear hovered states on close
          desktopMenu.setHoveredDesktopMenu(null);
          desktopMenu.setHoveredDesktopCategory(null);
        };

        const handleSkipToContentClick = () => {
          const mainContent = document.querySelector('main');
          if (mainContent) {
            // Ensure the element is focusable (add tabindex=-1 if it's not naturally focusable)
            if (mainContent.getAttribute('tabindex') === null) {
              mainContent.setAttribute('tabindex', '-1');
            }
            mainContent.focus();
          }
        };

        const killTimelines = () => {
          Object.values(timelines.value).forEach(safeKill);
          timelines.value = {};
        };

        const clearTimers = () => {
          Object.values(timers.value).forEach(safeKill);
          timers.value = {};
        };

        const cleanup = () => {
          killTimelines();
          clearTimers();
          destroyAllCarousels();
          gsap.killTweensOf(container);
          gsap.killTweensOf(container.querySelectorAll('*'));
        };

        const setInitialMenuStates = () => {
          const initialHiddenProps = { display: 'none', opacity: 0, pointerEvents: 'none' };
          if (elements.value.mainMenuContainer)
            gsap.set(elements.value.mainMenuContainer, initialHiddenProps);
          if (elements.value.level1Container)
            gsap.set(elements.value.level1Container, initialHiddenProps);
          if (elements.value.level2Container)
            gsap.set(elements.value.level2Container, initialHiddenProps);
          if (elements.value.allSubmenuPanels?.length)
            gsap.set(elements.value.allSubmenuPanels, initialHiddenProps);
          if (elements.value.mobileMenuOverlay)
            gsap.set(elements.value.mobileMenuOverlay, { scaleY: 0 });
          gsap.set(elements.value.mobileMenu, { pointerEvents: 'none' });
        };

        const handleScroll = () => {
          const currentScrollY = window.scrollY;
          const scrollThreshold = 60; // Minimum scroll distance to trigger hide/show

          // Handle header visibility
          if (Math.abs(currentScrollY - lastScrollY.value) >= scrollThreshold) {
            if (currentScrollY > lastScrollY.value && currentScrollY > headerHeight.value) {
              // Scrolling down past the header height
              isHeaderHiddenByScroll.value = true;
              // Reset the mouse near top state when header is hidden by scroll
              isMouseNearTop.value = false;
            } else if (currentScrollY < lastScrollY.value) {
              // Scrolling up - but don't show header if in scroll jack section
              if (!isInScrollJackSection.value) {
                isHeaderHiddenByScroll.value = false;
              }
            } else if (currentScrollY <= headerHeight.value) {
              // Scrolled back to top or within header height
              isHeaderHiddenByScroll.value = false;
            }
            lastScrollY.value = Math.max(0, currentScrollY);
          }

          // Handle desktop menu close on scroll
          if (isLarge.value && desktopMenu.isDesktopMenuOpen.value) {
            closeDesktopMenuHandler();
          }
        };

        /** Calculates and updates the header height ref. */
        const calculateHeaderHeight = () => {
          if (elements.value.headerBar) {
            headerHeight.value = elements.value.headerBar.offsetHeight;
          }
        };

        // Add a new handler for category mouse leave
        const handleCategoryMouseLeave = () => {
          if (!isLarge.value) return;
          // Clear the hover timer and the hovered category state
          safeKill(timers.value.categoryHover);
          desktopMenu.setHoveredDesktopCategory(null);
        };

        const handleMouseMove = (event) => {
          if (!isHeaderHiddenByScroll.value) return;

          // If mouse is near top, show header.
          if (event.clientY <= MOUSE_TRIGGER_THRESHOLD) {
            isMouseNearTop.value = true;
          }
        };

        return {
          activeDesktopCategory: desktopMenu.activeDesktopCategory,
          activeDesktopMenu: desktopMenu.activeDesktopMenu,
          cartCount,
          cartVisibility,
          handleCategoryMouseEnter,
          handleCategoryMouseLeave,
          handleDesktopMenuMouseEnter,
          handleDesktopMenuMouseLeave,
          handlePanelMouseEnter,
          handlePanelMouseLeave,
          handleSkipToContentClick,
          hoveredDesktopCategory: desktopMenu.hoveredDesktopCategory,
          hoveredDesktopMenu: desktopMenu.hoveredDesktopMenu,
          isAccordionMenuOpen: mobileMenu.isAccordionOpen,
          isDesktopMenuOpen: desktopMenu.isDesktopMenuOpen,
          isLarge,
          isMobileOpen: mobileMenu.isMobileOpen,
          showCartCount,
          toggleAccordionMenu: toggleMobileAccordionMenu,
          toggleMobileMenu: toggleMobileMenuHandler,
          toggleSelectedMenu: toggleMobileSelectedMenu,
          toggleSelectedSubMenu: toggleMobileSelectedSubMenu,
        };
      },
    }).mount(container);
  }

  onUnload(instanceData) {
    const app = instanceData.container.__vue_app__;
    if (app) {
      app.unmount();
      delete instanceData.container.__vue_app__;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new Header();
});
